<template>
  <!-- MBR041 Reward Catalogue — member_app_UI/song_fa_members_rewards_catalogue -->
  <div class="page-header align-items-start min-vh-screen mbr-cat">
    <span class="mask bg-white"></span>
    <div class="container p-0">
      <header class="mbr-cat__bar">
        <div class="mbr-cat__head">
          <h1 class="mbr-cat__title">{{ $t('reward.title') }}</h1>
          <span class="mbr-cat__emblem" aria-hidden="true">
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </span>
        </div>
        <!-- Catalogue is this screen; the other two segments navigate, nothing else changes there -->
        <nav class="mbr-cat__segment" :aria-label="$t('reward.sections')">
          <button type="button" class="mbr-cat__seg" @click="go('/my-rewards')">
            {{ $t('reward.tab_my_vouchers') }}
          </button>
          <button type="button" class="mbr-cat__seg mbr-cat__seg--active">
            {{ $t('reward.tab_catalogue') }}
          </button>
          <button type="button" class="mbr-cat__seg" @click="go('/store')">
            {{ $t('reward.tab_store') }}
          </button>
        </nav>
      </header>

      <main class="mbr-cat__body" v-if="isLoadDone">
        <MbrWalletCard :amount="merchantMember.amount_format">
          <!-- MBR106 point merchants only, exactly as before; was a second tile -->
          <template v-if="merchantMember.reward_type == rewardPoint" #side>
            <span class="mbr-wallet__eyebrow">{{ $t('reward.your_x', { name: pointName }) }}</span>
            <div class="mbr-wallet__small">{{ pointValue }} {{ pointName }}</div>
          </template>
        </MbrWalletCard>

        <div v-if="merchantMember.share_info_profile == 0" class="mbr-cat__profile">
          <span class="mbr-cat__profile-icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
          </span>
          <div class="mbr-cat__profile-text">
            <p class="mbr-cat__profile-copy">
              {{ $t('home.complete_profile') }}
            </p>
            <button type="button" class="mbr-cat__profile-cta" @click="editProfile()">
              {{ $t('home.add_profile') }}
            </button>
          </div>
        </div>

        <div class="mbr-cat__tools">
          <label class="mbr-cat__search">
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path stroke-linecap="round" d="M21 21l-4.35-4.35" /></svg>
            <input
              id="keyword_search"
              type="search"
              name="keyword_search"
              :placeholder="$t('catalogue.search_placeholder')"
              v-model="searchKeyword"
            />
          </label>
          <!-- Sort / Filter are built here rather than in the shared SortFilterToolbar,
               which Store still renders; they open the same two sheets -->
          <button
            type="button"
            class="mbr-cat__tool"
            :aria-label="$t('sort.title')"
            data-bs-toggle="modal"
            data-bs-target="#bottom_modal_sort"
          >
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3 4h18M6 10h12M9 16h6" /></svg>
          </button>
          <button
            type="button"
            class="mbr-cat__tool"
            :class="{ 'mbr-cat__tool--on': hasFilter }"
            :aria-label="$t('filter.title')"
            data-bs-toggle="modal"
            data-bs-target="#bottom_modal_filter"
          >
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
            <span v-if="hasFilter" class="mbr-cat__dot" aria-hidden="true"></span>
          </button>
        </div>

        <div v-if="visibleVouchers.length > 0" class="mbr-cat__grid">
          <article
            v-for="(value, index) in visibleVouchers"
            :key="index"
            class="mbr-cat__card"
            :class="{ 'mbr-cat__card--off': value.is_disabled }"
          >
            <div class="mbr-cat__thumb" @click="rewardDetail(value.id)">
              <img :src="value.icon_url" :alt="value.reward_title" />
              <span v-if="value.is_disabled" class="mbr-cat__lock" aria-hidden="true">
                <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              </span>
            </div>
            <div class="mbr-cat__text" @click="rewardDetail(value.id)">
              <h4 class="mbr-cat__name">{{ value.reward_title }}</h4>
              <div class="mbr-cat__cost">
                <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /></svg>
                <span>{{ costText(value) }}</span>
              </div>
              <p v-if="value.expiry_date_text" class="mbr-cat__valid">
                {{ validityText(value.expiry_date_text, 'reward') }}
              </p>
              <p v-if="minSpendText(value)" class="mbr-cat__valid">{{ minSpendText(value) }}</p>
            </div>
            <button
              v-if="!value.is_disabled"
              type="button"
              class="mbr-cat__get"
              data-bs-toggle="modal"
              data-bs-target="#bottom_modal"
              @click="exchange(value)"
            >
              {{ $t('common.get') }}
            </button>
            <!-- is_disabled also covers tier and usability limits, so only claim a
                 shortfall when the balance really is short -->
            <button v-else type="button" class="mbr-cat__get mbr-cat__get--off" disabled>
              {{ shortfallText(value) }}
            </button>
          </article>
        </div>
        <div v-else class="mbr-cat__empty">
          {{ searchKeyword.trim() ? $t('catalogue.no_match') : $t('home.no_rewards') }}
        </div>
      </main>

      <div class="position-fixed top-1 end-1 z-index-2">
        <material-snackbar
          v-if="snackbar === 'danger' || snackbar === 'success'"
          title=""
          date=""
          :description="message"
          :icon="{ component: 'campaign', color: 'white' }"
          :color="snackbar"
          :close-handler="closeSnackbar"
        />
      </div>
    </div>
    <!-- Modal -->
    <Sort :filterData="filterData" :changeSortType="changeSortType" />
    <Filter
      :filterRewardType="filterRewardType"
      :filterArrRewardType="filterArrRewardType"
      :stores="stores"
      :filterStore="filterStore"
      :filterArrStore="filterArrStore"
      :filterData="filterData"
      :resetData="resetData"
    />
    <Exchange
      :rewardPerTransaction="merchantMember.reward_type ?? 0"
      :voucherData="previewVoucherExchange"
      :amount_format="merchantMember.amount_format"
      :amount_in_account="merchantMember.amount_in_account"
      :point_format="merchantMember.point_format"
      :point_in_account="merchantMember.point"
      :is_disabled="previewVoucherExchange.is_disabled"
      :is_disabled_by_card="previewVoucherExchange.is_disabled_by_card"
      :confirm-exchange="confirmExchange"
    />
    <material-loading :active="loaderActive" />
    <ModalConfirm :message="$t('reward.confirm_exchange')"
            :btnYes="$t('common.yes')" :sycn-profile="processExchange" />
    <ModalSuccess
      :processAcction="gotoMyReward"
      :message="$t('reward.exchange_success')"
      :buttonText="$t('reward.go_to_my_reward')"
    />
    <QrcodeMember />
    <MbrBottomNav />
  </div>
</template>

<script>
import { mapMutations, mapActions, mapState } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import RewardService from "../services/reward.service";
import "bootstrap/dist/js/bootstrap.bundle";
import Exchange from "@/components/Exchange.vue";
import Sort from "@/components/Sort.vue";
import Filter from "@/components/Filter.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import ModalSuccess from "@/components/ModalSuccess.vue";
import ModalConfirm from "@/components/ModalConfirm.vue";
import QrcodeMember from "@/components/QrcodeMember.vue";
import MbrBottomNav from "@/components/MbrBottomNav.vue";
import MbrWalletCard from "@/components/MbrWalletCard.vue";
import "bootstrap/js/dist/modal";
import $ from "jquery";
import HomeService from "../services/home.service";
import { getMerchantCode } from "@/config/utils";
import { validityText } from "@/i18n/apiText";
import { errorMessage } from "@/utils/errorMessage";

export default {
  name: "my-transacions",
  components: {
    MaterialSnackbar,
    Exchange,
    MaterialLoading,
    ModalSuccess,
    Sort,
    Filter,
    ModalConfirm,
    QrcodeMember,
    MbrBottomNav,
    MbrWalletCard,
  },
  data() {
    return {
      listTransaction: [],
      membershipCashback: 0,
      transactionType: [],
      merchantVouchers: [],
      currentMember: {},
      merchantMember: {},
      previewVoucherExchange: {},
      snackbar: null,
      loaderActive: false,
      merchantCode: "",
      stores: [],
      filterArrRewardType: [],
      filterArrStore: [],
      params: {},
      sortType: "1",
      isLoadDone: false,
      pointName: 'Point',
      pointIcon: '',
      // reward_per_transaction: 1 cashback, 2 point
      rewardCashback: 1,
      rewardPoint: 2,
      // client-side: POST /member/rewards has no search param and returns the
      // whole list unpaginated
      searchKeyword: "",
    };
  },
  created() {
    this.getDataHomePage();
    this.getBranches();

    var session_id = this.$route.query.session_id;
    var reward_id = this.$route.query.reward_id;
    if(session_id && reward_id) { // call back after pay success - stripe
      this.showLoader();
      this.isLoadDone = false;
      HomeService.processExchangeByCard(reward_id, session_id).then(
        (response) => {
          if (response.data.result.isSuccess) {
            // const merchantCode = localStorage.getItem('MERCHANT_CODE', '');
            // var url = location.protocol + '//' + location.host+"/"+merchantCode+"/home";
            // window.location.href = url;
            this.getDataHomePage();
            this.getBranches();
            this.snackbar = "success";
            this.message = this.$t('reward.exchange_successfully');
            this.setTimeOutNotification();
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.isLoadDone = true;
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    }
  },

  // draws its own header like the design: hide the top navbar, and keep the
  // sidenav as an off-screen drawer so MbrBottomNav's ACCOUNT can slide it in
  beforeMount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.add("mbr-sidenav-overlay");
    app.classList.remove("g-sidenav-pinned");
  },

  beforeUnmount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.remove("mbr-sidenav-overlay");
    if (window.innerWidth > 1200) {
      app.classList.add("g-sidenav-pinned");
    }
  },

  computed: {
    // point_format is "8 Point"; the tile prints the number and the name apart,
    // so take the balance itself, truncated the way Helper::formatPoint does
    pointValue() {
      const point = Number(this.merchantMember.point);
      return isFinite(point) ? Math.trunc(point).toLocaleString("en-US") : "0";
    },

    hasFilter() {
      return this.filterArrRewardType.length > 0 || this.filterArrStore.length > 0;
    },

    visibleVouchers() {
      const keyword = this.searchKeyword.trim().toLowerCase();
      if (!keyword) {
        return this.merchantVouchers;
      }
      return this.merchantVouchers.filter((voucher) =>
        (voucher.reward_title ?? "").toLowerCase().includes(keyword)
      );
    },

    // leading symbol of merchant_member.amount_format ("S$ 1,668.19" -> "S$")
    currencySymbol() {
      const symbol = (this.merchantMember.amount_format ?? "").match(/^[^\d]+/);
      return symbol ? symbol[0].trim() : "$";
    },
  },

  mounted() {
    const code = getMerchantCode();
    this.merchantCode = code ? `/${code}` : "";
  },
  methods: {
    validityText,
    ...mapMutations([
      "toggleEveryDisplay",
      "toggleHideConfig",
      "toggleNavbarOnly",
      "navbarMinimize",
    ]),
    ...mapActions(["setHeaderColor", "setLabelColor", "setButtonColor"]),
    ...mapState(["isPinned"]),
    go(path) {
      this.$router.push(this.merchantCode + path);
    },

    gotoMyReward() {
      $(".btn-close").click();
      this.$router.push(this.merchantCode + "/my-rewards");
    },
    toggleSidebar() {
      this.navbarMinimize();
    },
    editProfile() {
      this.$router.push(this.merchantCode + "/profile");
    },

    // point merchants price vouchers in points, cashback merchants in money
    costText(voucher) {
      return this.merchantMember.reward_type == this.rewardPoint
        ? voucher.point_text
        : voucher.amount_text;
    },

    // order_minimum 2 = MintyMerReward::ORDER_MINIMUM_YES ("Order Total")
    minSpendText(voucher) {
      return voucher.order_minimum == 2 && Number(voucher.order_total) > 0
        ? this.$t('reward.min_spend', { amount: this.currencySymbol + parseFloat(Number(voucher.order_total).toFixed(2)) })
        : "";
    },

    // is_disabled is true for three different reasons (balance, tier, usability
    // spent), so only name a shortfall when the balance is actually short
    shortfallText(voucher) {
      const isPoint = this.merchantMember.reward_type == this.rewardPoint;
      const cost = Number(isPoint ? voucher.points_convert : voucher.amount_convert);
      const balance = Number(
        isPoint
          ? this.merchantMember.point
          : // MBR094: top-up money cannot pay for a voucher
            this.merchantMember.cashback_can_exchange ?? this.merchantMember.amount_in_account
      );
      if (!isFinite(cost) || !isFinite(balance)) {
        return this.$t('reward.unavailable');
      }
      // the API compares the two as integers, so mirror that here
      const missing = Math.trunc(cost) - Math.trunc(balance);
      if (missing <= 0) {
        return this.$t('reward.unavailable');
      }
      return isPoint
        ? this.$t('reward.need_more', { amount: missing })
        : this.$t('reward.need_more', { amount: this.currencySymbol + missing });
    },

    exchange(voucher) {
      this.previewVoucherExchange = voucher;
    },

    closeSnackbar() {
      this.snackbar = null;
    },

    getDataHomePage() {
      this.closeSnackbar();
      this.getRewardCatalogue(this.params);
    },

    rewardDetail(reward_id) {
      this.$router.push(this.merchantCode + "/reward-detail/" + reward_id);
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },

    getRewardCatalogue(params) {
      this.showLoader();
      this.isLoadDone = false;
      RewardService.getList(params).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.merchantVouchers = response.data.data.vouchers;
            this.currentMember = response.data.data.member;
            this.merchantMember = response.data.data.merchant_member;
            this.pointName = response.data.data.point_name;
            this.pointIcon = response.data.data.point_icon;
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.isLoadDone = true;
          this.hideLoader();
        },
        (error) => {
          this.isLoadDone = true;
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    },

    confirmExchange() {
      $('.btn-confirm-modal').click();
    },

    processExchange() {
      $('.btn-close-confirm-modal').click();
      this.closeSnackbar();
      this.showLoader();
      const rewardId = this.previewVoucherExchange.id ?? 0;
      RewardService.processExchange(rewardId).then(
        (response) => {
          if (response.data.result.isSuccess) {
            $(".btn-close").click();
            this.getRewardCatalogue();
            $(".btn-close").click();
            $(".btn-success-modal").click();
          } else {
            $(".btn-close").click();
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    },

    changeSortType(event) {
      this.sortType = event.target.value;
    },

    filterRewardType(value) {
      const index = this.filterArrRewardType.indexOf(value);
      if (index > -1) {
        this.filterArrRewardType.splice(index, 1);
      } else {
        this.filterArrRewardType.push(value);
      }
    },

    filterStore(value) {
      const index = this.filterArrStore.indexOf(value);
      if (index > -1) {
        this.filterArrStore.splice(index, 1);
      } else {
        this.filterArrStore.push(value);
      }
    },

    getBranches() {
      this.stores = [];
      this.showLoader();
      RewardService.getBranches().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.stores = response.data.data;
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    filterData() {
      $(".btn-close").click();
      $(".x-close").click();
      this.params = {
        sort_type: this.sortType,
        filter_reward_type: this.filterArrRewardType,
        filter_store: this.filterArrStore,
      };
      this.getRewardCatalogue(this.params);
    },
    resetData() {
      $(".btn-unclick").css({ "background-color": "#E3EBF7", color: "#000" });
      $(".btn-unclick2").css({ "background-color": "#E3EBF7", color: "#000" });
      this.filterArrRewardType = [];
      this.filterArrStore = [];
    },

    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
  },
};
</script>
<style scoped>
/* MBR041 Reward Catalogue — member_app_UI/song_fa_members_rewards_catalogue.
   The old unscoped block lived here (`* { font-family: Poppins }`, the ticket
   `.card`, `.max-line`, `a.has-sub`…); the parts other screens relied on now
   live in assets/css/custom.css, moved there when Store.vue dropped its copy
   (MBR044). */

/* Material Dashboard's `.page-header { overflow: hidden }` would stop the header sticking */
.mbr-cat.page-header {
  overflow: visible;
  overflow-x: clip;
  /* room for the fixed MbrBottomNav */
  padding-bottom: 120px;
  font-family: var(--mbr-font-body);
}

/* custom.css still ships an app-wide `* { font-family: Poppins }`;
   :where() keeps this at one attribute's specificity so the class rules below still win */
.mbr-cat :where(:not(.material-icons-round)) {
  font-family: var(--mbr-font-body);
}

.mbr-cat .mask {
  background: var(--mbr-color-canvas) !important;
  opacity: 1;
}

/* mobile column, like Home, Account and My Vouchers */
.mbr-cat .container {
  max-width: 440px;
  margin: 0 auto;
}

.mbr-cat__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 24px 20px 12px;
  background: rgba(var(--mbr-color-canvas-rgb), 0.95);
  border-bottom: 1px solid rgba(var(--mbr-color-outline-rgb), 0.3);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.mbr-cat__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-cat__title {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 24px;
  font-weight: 800;
  line-height: 32px;
  letter-spacing: -0.02em;
  color: var(--mbr-color-ink-headline);
}

.mbr-cat__emblem {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-primary);
}

.mbr-cat__emblem svg {
  width: 16px;
  height: 16px;
}

.mbr-cat__segment {
  display: flex;
  margin-top: 12px;
  padding: 4px;
  background: var(--mbr-color-surface);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-cat__seg {
  flex: 1 1 0;
  padding: 8px 4px;
  background: none;
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
  color: var(--mbr-color-ink-secondary);
  transition: background 0.15s ease, color 0.15s ease;
}

.mbr-cat__seg--active {
  background: var(--mbr-color-primary);
  font-weight: 800;
  color: var(--mbr-color-on-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
}

.mbr-cat__body {
  padding: 16px 20px 0;
}

.mbr-cat__profile {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 16px;
  padding: 14px;
  background: var(--mbr-color-card);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  border-radius: var(--mbr-radius-lg);
  box-shadow: var(--mbr-shadow-card);
}

.mbr-cat__profile-icon {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-md);
  color: var(--mbr-color-primary);
}

.mbr-cat__profile-icon svg {
  width: 24px;
  height: 24px;
}

.mbr-cat__profile-text {
  min-width: 0;
}

.mbr-cat__profile-copy {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  color: var(--mbr-color-ink-headline);
}

.mbr-cat__profile-cta {
  margin-top: 8px;
  padding: 6px 18px;
  background: var(--mbr-color-primary);
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 800;
  line-height: 18px;
  color: var(--mbr-color-on-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
  transition: transform 0.12s ease;
}

.mbr-cat__profile-cta:active {
  transform: scale(0.97);
}

/* MBR042 — search + sort/filter row */
.mbr-cat__tools {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
}

.mbr-cat__search {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 8px 14px;
  background: var(--mbr-color-tint-dark);
  border: 1px solid transparent;
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-ink-secondary);
  transition: border-color 0.15s ease;
}

.mbr-cat__search:focus-within {
  border-color: var(--mbr-color-primary);
}

.mbr-cat__search svg {
  flex: none;
  width: 16px;
  height: 16px;
  opacity: 0.7;
}

/* outranks the global `input.nosubmit` / `input[type=search]` rules in custom.css */
.mbr-cat__search input[type="search"] {
  width: 100%;
  min-width: 0;
  padding: 0;
  background: none;
  border: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  color: var(--mbr-color-ink-headline);
}

.mbr-cat__search input[type="search"]:focus {
  border: 0 !important;
  outline: none;
}

.mbr-cat__search input[type="search"]::placeholder {
  color: var(--mbr-color-ink-muted);
  text-align: left;
  opacity: 1;
}

.mbr-cat__tool {
  position: relative;
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: var(--mbr-color-surface);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.7);
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-ink-headline);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  transition: transform 0.12s ease;
}

.mbr-cat__tool:active {
  transform: scale(0.95);
}

.mbr-cat__tool svg {
  width: 16px;
  height: 16px;
}

.mbr-cat__tool--on {
  border-color: var(--mbr-color-primary);
  color: var(--mbr-color-primary);
}

.mbr-cat__dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 7px;
  height: 7px;
  background: var(--mbr-color-primary);
  border: 1.5px solid var(--mbr-color-surface);
  border-radius: var(--mbr-radius-full);
}

/* MBR042 — two-column reward grid */
.mbr-cat__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.mbr-cat__card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--mbr-color-card);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  border-radius: 16px;
  box-shadow: var(--mbr-shadow-card);
  cursor: pointer;
}

.mbr-cat__card--off {
  opacity: 0.85;
}

.mbr-cat__thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: var(--mbr-color-tint);
  border-radius: 12px;
}

.mbr-cat__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mbr-cat__lock {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: rgba(var(--mbr-color-surface-rgb), 0.85);
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-ink-secondary);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.mbr-cat__lock svg {
  width: 14px;
  height: 14px;
}

.mbr-cat__text {
  flex: 1 1 auto;
  min-width: 0;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h4 */
.mbr-cat__name {
  margin: 0;
  overflow: hidden;
  font-family: var(--mbr-font-headline);
  font-size: 13px;
  font-weight: 800;
  line-height: 18px;
  letter-spacing: normal;
  color: var(--mbr-color-ink-headline);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.mbr-cat__cost {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 800;
  line-height: 16px;
  color: var(--mbr-color-primary);
}

.mbr-cat__card--off .mbr-cat__cost {
  color: var(--mbr-color-ink-secondary);
}

.mbr-cat__cost svg {
  flex: none;
  width: 14px;
  height: 14px;
}

.mbr-cat__valid {
  margin: 2px 0 0;
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  color: rgba(var(--mbr-color-ink-secondary-rgb), 0.7);
}

.mbr-cat__get {
  width: 100%;
  padding: 6px 8px;
  background: var(--mbr-color-primary);
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 800;
  line-height: 18px;
  color: var(--mbr-color-on-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
  transition: transform 0.12s ease, opacity 0.12s ease;
}

.mbr-cat__get:active {
  transform: scale(0.97);
}

.mbr-cat__get--off {
  background: var(--mbr-color-tint-dark);
  font-size: 11px;
  color: rgba(var(--mbr-color-ink-secondary-rgb), 0.6);
  box-shadow: none;
  cursor: not-allowed;
}

.mbr-cat__empty {
  margin-top: 16px;
  padding: 40px 20px;
  background: var(--mbr-color-tint);
  border: 1px dashed var(--mbr-color-outline);
  border-radius: var(--mbr-radius-lg);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--mbr-color-ink-secondary);
  text-align: center;
}
</style>
